/* eslint-disable @typescript-eslint/no-unused-vars */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useDebouncedCallback } from 'use-debounce';
import {
  Grid,
  Link,
  Card,
  Alert,
  Snackbar,
  Theme,
  Tooltip,
  IconButton
} from '@mui/material';

import {
  MDBox,
  Chart,
  MDTypography,
  FullPageLoader,
  Footer,
  // DefaultNavbar,
  FaIcon,
  If,
  Configurator,
  CommentsList
} from '@components';
import ChartHeader from './components/ChartHeader';

import { RootState } from '@store/store';
import {
  useCreateChartCommentMutation,
  useGetPreviewChartByIdQuery,
  useGetChartCommentsQuery
} from '@api/chart';
import { setChartData } from '@pages/ChartPreview/slice';
import { useLazyGetProjectByIdQuery } from '@api/projects';
import { setCurrentProjectData } from '@pages/ProjectBoard/slice';
import { VisualSettings } from '@DStypes/chart';
import {
  setOpenConfigurator,
  useMaterialUIController
} from '@context/MDContext';

import { initAmplitude, sendAmplitudeData } from '@utils/amplitude';
import { sendGAData } from '@utils/googleAnalytics';
import { getChartTheme } from '@utils/chart';
import { getCurrentFilter } from '@utils/common';
import useCustomNavigate from '@hooks/useCustomNavigate';
import useCharts from '@hooks/useCharts';
import breakpoints from '@theme/base/breakpoints';
import {
  DATE_GROUP,
  DATE_TIME_GROUP,
  numberEquivalents
} from '@constants/common';
import { PAGE_VISITED, VIEW_CHART } from '@constants/eventNames';
import { GO_BACK_MESSAGE } from '@constants/toolTips';

import brandLogo from '@assets/images/DS_Logo.png';
import { PricingPlans } from '@constants/pricing';

const commonBoxStyles = ({ palette }: Theme) => ({
  display: 'flex',
  alignItems: 'center',
  textAlign: 'center',
  height: 'fit-content',
  width: 'auto',
  border: `2px solid ${palette.secondary.main}`,
  paddingX: 1
});

const formatCommonBoxText = (text: string) => {
  if (text.length <= 50) return text;
  else return `${text.slice(0, 50)}...`;
};

const ChartPreview: React.FC = () => {
  const { chartId, collaborationId, projectId } = useParams();
  const navigate = useCustomNavigate();
  const reduxDispatch = useDispatch();
  const [controller, dispatch] = useMaterialUIController();
  const { openConfigurator, preventClosing } = controller;

  const chartData = useSelector(
    (state: RootState) => state.projectBoard.data?.[chartId as string]
  );
  const currentChartPreviewData = useSelector(
    (state: RootState) => state?.currentChartPreviewData
  );

  const { data: chartConfigData, isLoading: isGetChartsLoading } =
    useGetPreviewChartByIdQuery({
      id: chartId,
      collaboration_id: collaborationId
    });
  const { getChartData } = useCharts({ isPreview: true });
  const [createComment] = useCreateChartCommentMutation();
  const { data: commentsList } = useGetChartCommentsQuery(chartId);
  const [getProjectById, { data: projectData }] = useLazyGetProjectByIdQuery();

  const [openSuccessSnackBar, setOpenSuccessSnackBar] = useState(true);
  const [mobileView, setMobileView] = useState<boolean>(false);
  const [dropDownValues, setDropDownValues] = useState({
    chart_type: '',
    chart_operation: '',
    x_axis: '',
    y_axis: '',
    segment_field: ''
  });
  const [visualSettings, setVisualSettings] = useState<VisualSettings | null>(
    null
  );
  const [configuratorFlag, setConfiguratorFlag] = useState<
    'settings' | 'comments' | ''
  >('');
  const chartType =
    dropDownValues.chart_type || chartConfigData?.config?.chart_type;

  const xAxisType = chartConfigData?.config?.x_axis?.treat_as_type;

  const activeDateGroup =
    chartConfigData?.config.date_group === 'none'
      ? xAxisType === 'DATETIME'
        ? DATE_TIME_GROUP.HOURS
        : DATE_GROUP.DAYS
      : chartConfigData?.config.date_group;

  const goBackDestination = `/project/${projectId}/${collaborationId}/preview`;

  const config = useMemo(() => {
    if (chartConfigData) return JSON.parse(chartConfigData.config.chart_config);

    return null;
  }, [chartConfigData]);

  const getChart = useMemo<JSX.Element | null>(() => {
    const chartType =
      dropDownValues.chart_type || chartConfigData?.config?.chart_type;

    const segment_field =
      dropDownValues?.segment_field || chartConfigData?.config?.segment_field;

    let xDateItemsToRender = 60;

    if (window.innerWidth > 1400) xDateItemsToRender = 60;
    else if (window.innerWidth > 1024) xDateItemsToRender = 50;
    else if (window.innerWidth > 768) xDateItemsToRender = 30;
    else if (window.innerWidth > 400) xDateItemsToRender = 15;
    else xDateItemsToRender = 12;

    const selfChart =
      chartConfigData?.config?.x_axis.id ===
      chartConfigData?.config?.y_axis?.id;

    const isNumber = numberEquivalents.includes(xAxisType);

    const isYPercentage =
      chartConfigData?.config?.y_axis?.treat_as_type === 'PERCENTAGE'
        ? true
        : false;

    const isXPercentage =
      chartConfigData?.config?.x_axis?.treat_as_type === 'PERCENTAGE'
        ? true
        : false;

    const xAxisField = chartConfigData?.config?.x_axis?.fieldname || '';

    const showWaterMark = ![PricingPlans.PRO, PricingPlans.PRO_PLUS].includes(
      projectData?.shared_user_plan
    );

    if (chartType && config && chartData)
      return (
        <Chart
          chartId={chartConfigData?.id}
          chartConfig={config}
          data={chartData?.data}
          width='100%'
          height='100%'
          chartType={chartType}
          visualSettings={visualSettings || null}
          isSegmented={segment_field}
          colorSet={getChartTheme(chartConfigData)}
          dateGroup={activeDateGroup}
          isDate={xAxisType === 'DATE' || xAxisType === 'DATETIME'}
          isShareView
          xDateItemsToRender={xDateItemsToRender}
          isSelfChart={selfChart}
          isNumber={isNumber}
          isYPercentage={isYPercentage}
          isXPercentage={isXPercentage}
          showWaterMark={showWaterMark}
          skipEmptyPeriods={chartConfigData?.config?.date_group === 'none'}
          xAxisField={xAxisField}
        />
      );

    return null;
  }, [chartData, config, visualSettings]);

  useEffect(() => {
    setOpenConfigurator(dispatch, false);
    sendGAData(PAGE_VISITED);
    initAmplitude();
    sendAmplitudeData(PAGE_VISITED);
    sendAmplitudeData(VIEW_CHART);
    // A function that sets the display state for the DefaultNavbarMobile.
    function displayMobileNavbar() {
      if (window.innerWidth < breakpoints.values.lg) setMobileView(true);
      else setMobileView(false);
    }

    /** The event listener that's calling the displayMobileNavbar function when resizing the window.
     */
    window.addEventListener('resize', displayMobileNavbar);

    // Call the displayMobileNavbar function to set the state with the initial value.
    displayMobileNavbar();

    // Remove event listener on cleanup
    return () => window.removeEventListener('resize', displayMobileNavbar);
  }, []);

  useEffect(() => () => setOpenConfigurator(dispatch, false), []);
  const cardRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    setOpenSuccessSnackBar(true);
    initAmplitude();
  }, []);

  useEffect(() => {
    if (chartConfigData?.chart_project)
      getProjectById({
        projectId: chartConfigData.chart_project,
        collaboration_id: collaborationId
      });
  }, [chartConfigData]);

  useEffect(() => {
    reduxDispatch(setCurrentProjectData(projectData));
  }, [projectData]);

  useEffect(() => {
    if (chartConfigData) {
      reduxDispatch(
        setChartData({
          ...chartConfigData
        })
      );

      setDropDownValues({
        chart_type: chartConfigData.config.chart_type,
        chart_operation: chartConfigData.config.chart_operation,
        x_axis: chartConfigData.config.x_axis.fieldname,
        y_axis:
          chartConfigData.config.x_axis.fieldname ===
            chartConfigData.config.y_axis.fieldname &&
          chartConfigData.config.chart_type !== 'KPI'
            ? 'Rows'
            : chartConfigData.config.y_axis.fieldname,
        segment_field: chartConfigData.config.segment_field?.fieldname || null
      });
    }
  }, [chartConfigData]);

  useEffect(() => {
    if (chartConfigData && projectData) {
      const {
        x_axis,
        y_axis,
        segment_field,
        chart_operation,
        visual_settings,
        date_group,
        filters,
        dynamic_filters
      } = chartConfigData.config;

      const default_theme_connection_status = {
        '3d_charts': true,
        gradient_charts: true,
        multicolor_charts: true,
        colorThemeToggle: true
      };
      const updatedVisualSettings = { ...visual_settings };

      if (!visual_settings?.theme_connection_status)
        updatedVisualSettings.theme_connection_status =
          default_theme_connection_status;

      const currentFilter = getCurrentFilter({
        projectFilters:
          Object.keys(projectData?.filters || {})?.length > 0 ||
          Object.keys(projectData?.dynamic_filters || {})?.length > 0,
        toggleDashboardFilter:
          chartConfigData.config.visual_settings.toggleDashboardFilter,
        chartGlobalFilterToggle:
          chartConfigData.config.visual_settings.chartGlobalFilterToggle,
        chartFilters:
          Object.keys(filters || {})?.length > 0 ||
          Object.keys(dynamic_filters || {})?.length > 0
      });

      setVisualSettings(updatedVisualSettings);
      getChartData({
        x_axis: x_axis?.id,
        y_axis: y_axis?.id,
        chart_operation: chart_operation,
        segment_field: segment_field?.id || null,
        id: chartConfigData.id,
        date_group,
        empty_values_included: chartConfigData.config.visual_settings
          .showEmptyValues
          ? 'True'
          : 'False',
        percentage_format: chartConfigData.config.visual_settings
          .showAsPercentage
          ? 'true'
          : 'false',
        auto_group_numbers: chartConfigData.config.visual_settings
          .autoGroupNumbers
          ? 'True'
          : 'False',
        force: true,
        is_kpi: chartConfigData.config.chart_type === 'KPI' ? 'True' : 'False',
        chart_type: chartConfigData.config.chart_type,
        collaboration_id: collaborationId,
        ...(currentFilter !== 'NONE' && {
          filters: currentFilter === 'PROJECT' ? projectData?.filters : filters,
          dynamic_filters:
            currentFilter === 'PROJECT'
              ? projectData?.dynamic_filters
              : dynamic_filters
        })
      });
    }
  }, [chartConfigData, projectData]);

  const onCreateChartComment = (commentDetails: any) => {
    if (chartId && commentDetails)
      createComment({
        id: chartId,
        payload: collaborationId
          ? { ...commentDetails, collaboration_id: collaborationId }
          : commentDetails
      });
  };

  const onOpenSlideIn = useDebouncedCallback(
    (active: 'comments' | 'settings', openConfigurator: boolean) => {
      if (configuratorFlag === '' || !openConfigurator) {
        setConfiguratorFlag(active);
        setOpenConfigurator(dispatch, true);
      } else {
        if (openConfigurator)
          if (configuratorFlag === active) {
            setOpenConfigurator(dispatch, false);
            setTimeout(() => {
              setConfiguratorFlag('');
            }, 400);
          } else {
            setOpenConfigurator(dispatch, false);
            setTimeout(() => {
              setConfiguratorFlag(active);
              setOpenConfigurator(dispatch, true);
            }, 300);
          }
      }
    },
    300
  );

  const onCloseConfigurator = () => {
    setOpenConfigurator(dispatch, false);
    setTimeout(() => {
      setConfiguratorFlag('');
    }, 400);
  };

  const handleCloseSnackbar = () => setOpenSuccessSnackBar(false);

  if (isGetChartsLoading) return <FullPageLoader />;

  return (
    <>
      <MDBox px={3} sx={{ overflowX: 'hidden' }}>
        {projectId && (
          <Grid
            display='flex'
            justifyContent='space-between'
            mb={projectId ? 3.5 : 8.5}
          >
              <Grid
                item
                justifySelf='flex-start'
                display={{ xs: 'none', sm: 'inline-block' }}
              >
                <Grid item mt={3}>
                  <Tooltip title={GO_BACK_MESSAGE} placement='top'>
                    <IconButton
                      onClick={() => {
                        goBackDestination
                          ? navigate(goBackDestination)
                          : navigate(-1);
                      }}
                    >
                      <FontAwesomeIcon icon={['far', 'left']} />
                    </IconButton>
                  </Tooltip>
                </Grid>
              </Grid>

            {/* <Grid item container flex={1} mb={1.8}>
              <DefaultNavbar
                routes={[]}
                brand={brandLogo}
                imageBrand={true}
                subtitle=' '
                subtitle='Powered by DataSquirrel! '
                action={{
                  type: 'external',
                  route: 'https://www.datasquirrel.ai/',
                  label: 'Sign Up!',
                  color: 'light',
                  textColor: 'info',
                  textTransform: 'uppercase',
                  variant: 'contained'
                }}
                toggleConfigurator={() =>
                  onOpenSlideIn('comments', openConfigurator)
                }
                showBeta={true}
              />
            </Grid>*/}
          </Grid>
        )}
        <Grid container flexDirection='column' height='90vh' wrap='nowrap'>
          <Grid item pl={0} mt={{ xs: '2.5rem', sm: '0.5rem' }}>
            <MDBox
            component={Link}
            href='https://datasquirrel.ai'
            target='_blank'
            >
              <MDBox
                  component='img'
                  src={brandLogo}
                  alt='Brand'
                  width={{ xs: '1.6rem', sm: '2.5rem' }}
              />
            </MDBox>
            <ChartHeader isPreview />
          </Grid>
          <Grid item>
            <Card
              sx={{
                padding: '8px 16px !important',
                my: { xs: 1.5 }
              }}
            >
              <MDBox
                display='flex'
                justifyContent='space-between'
                alignItems='center'
              >
                <Grid
                  container
                  spacing={1}
                  direction='row'
                  alignItems='center'
                  width='100%'
                >
                  {/* <Grid item justifySelf='flex-start'>
                    <Tooltip
                      title={'You do not have permission to edit this chart.'}
                      placement='top'
                    >
                      <MDBox
                        borderRadius='lg'
                        bgColor='white'
                        sx={commonBoxStyles}
                      >
                        <MDTypography
                          textTransform='uppercase'
                          variant='sm'
                          color='grey'
                        >
                          {dropDownValues.chart_type}
                        </MDTypography>
                      </MDBox>
                    </Tooltip>
                  </Grid> */}
                  <Grid
                    item
                    container
                    flex={1}
                    justifyContent='center'
                    alignItems='center'
                    spacing={1}
                  >
                    <Grid item>
                      <Tooltip
                        title={'You do not have permission to edit this chart.'}
                        placement='top'
                      >
                        <MDBox
                          borderRadius='lg'
                          bgColor='white'
                          sx={commonBoxStyles}
                        >
                          <MDTypography
                            textTransform='uppercase'
                            variant='sm'
                            color='grey'
                          >
                            {dropDownValues.chart_operation}
                          </MDTypography>
                        </MDBox>
                      </Tooltip>
                    </Grid>
                    <Grid item>
                      <MDTypography component='span' variant='sm' color='info'>
                        of
                      </MDTypography>
                    </Grid>
                    <Grid item>
                      <Tooltip
                        title={'You do not have permission to edit this chart.'}
                        placement='top'
                      >
                        <MDBox
                          borderRadius='lg'
                          bgColor='white'
                          sx={commonBoxStyles}
                        >
                          <MDTypography
                            textTransform='uppercase'
                            variant='sm'
                            color='grey'
                          >
                            {formatCommonBoxText(dropDownValues.y_axis)}
                          </MDTypography>
                        </MDBox>
                      </Tooltip>
                    </Grid>
                    {dropDownValues.chart_type !== 'KPI' && (
                      <>
                        <Grid item>
                          <MDTypography
                            component='span'
                            variant='sm'
                            color='info'
                          >
                            per
                          </MDTypography>
                        </Grid>
                        <Grid item>
                          <Tooltip
                            title={
                              'You do not have permission to edit this chart.'
                            }
                            placement='top'
                          >
                            <MDBox
                              borderRadius='lg'
                              bgColor='white'
                              sx={commonBoxStyles}
                            >
                              <MDTypography
                                textTransform='uppercase'
                                variant='sm'
                                color='grey'
                              >
                                {formatCommonBoxText(dropDownValues.x_axis)}
                              </MDTypography>
                            </MDBox>
                          </Tooltip>
                        </Grid>
                      </>
                    )}
                    {dropDownValues.chart_type !== 'PIE' &&
                      dropDownValues.chart_type !== 'KPI' &&
                      dropDownValues.segment_field !== null && (
                        <>
                          <Grid item>
                            <MDTypography
                              component='span'
                              variant='sm'
                              color='info'
                            >
                              split by
                            </MDTypography>
                          </Grid>
                          <Grid item>
                            <Tooltip
                              title={
                                'You do not have permission to edit this chart.'
                              }
                              placement='top'
                            >
                              <MDBox
                                borderRadius='lg'
                                bgColor='white'
                                sx={commonBoxStyles}
                              >
                                <MDTypography
                                  textTransform='uppercase'
                                  variant='sm'
                                  color='grey'
                                >
                                  {formatCommonBoxText(
                                    dropDownValues.segment_field
                                  ) || 'None'}
                                </MDTypography>
                              </MDBox>
                            </Tooltip>
                          </Grid>
                        </>
                      )}
                  </Grid>
                </Grid>
                <MDBox
                  display='flex'
                  sx={{ display: { xs: 'none', lg: 'inline-block' } }}
                >
                  <Tooltip title='View/Add chart comments' placement='bottom'>
                    <IconButton
                      onClick={() =>
                        onOpenSlideIn('comments', openConfigurator)
                      }
                      disabled={preventClosing}
                      classes={{ root: 'preventBgClose' }}
                    >
                      <FaIcon
                        icon='comment-dots'
                        typographyProps={{ color: 'dark', variant: 'xl' }}
                        style={{ pointerEvents: 'none' }}
                      />
                    </IconButton>
                  </Tooltip>
                </MDBox>
              </MDBox>
            </Card>
            <MDBox
              position={{ xs: 'fixed', lg: 'sticky' }}
              right={0}
              top={{ xs: '0px', lg: '32px' }}
              zIndex={10}
              mt={{ xs: '0', lg: 2 }}
            >
              <Configurator
                width='350px'
                isProjectBoard={false}
                configuratorFlag={configuratorFlag}
                onCloseConfigurator={() => onCloseConfigurator()}
              >
                <If condition={configuratorFlag === 'comments'}>
                  <CommentsList
                    commentsList={commentsList}
                    onPostComment={onCreateChartComment}
                    height={(cardRef?.current?.offsetHeight || 46) + 46}
                    isChartPage
                  />
                </If>
              </Configurator>
            </MDBox>
          </Grid>
          <Grid
            item
            // flex={1}
            sx={{
              width: '100%',
              paddingX: '0px !important',
              marginBottom: '2px',
              height: mobileView ? '500px' : '70vh'
            }}
          >
            <MDBox
              // position='relative'
              sx={{
                // height:
                //   window.innerHeight < 750 && hasLegends ? '140%' : '100%',
                overflow: 'auto',
                height: '100%',
                boxShadow: '0 10px 27px 0 rgb(0 0 0 / 5%)'
              }}
            >
              {/* <Card
                sx={{
                  padding: '16px',
                  backgroundColor: 'red',
                  height: '100%',
                  transition: 'width 0.5s',
                  width: {
                    xs: '100%',
                    lg: openConfigurator ? 'calc(100% - 360px)' : '100%'
                  }
                }}
                ref={cardRef}
              >
                {isGetChartsLoading ? (
                  <FullPageLoader />
                ) : (
                  <MDBox
                    sx={{
                      backgroundColor: '#ffffff',
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center'
                    }}
                    height='100%'
                  >
                    {getChart}
                  </MDBox>
                )}
              </Card> */}
              <Card
                sx={{
                  height: '100%',
                  transition: 'width 0.5s',
                  width:
                    openConfigurator && !mobileView
                      ? 'calc(100% - 370px)'
                      : '100%'
                }}
                ref={cardRef}
              >
                <div
                  style={{
                    position: 'relative',
                    height: '100%',
                    overflowY: 'auto',
                    overflowX: 'hidden'
                  }}
                >
                  {chartType !== 'KPI' && (
                    <div
                      style={{
                        height: '150px',
                        width: '100%',
                        position: 'absolute',
                        top: 5,
                        background: 'white'
                      }}
                    />
                  )}
                  <MDBox
                    sx={{
                      // backgroundColor: '#ffffff',
                      padding: '10px',
                      height: '100%'
                    }}
                  >
                    {getChart}
                  </MDBox>
                </div>
              </Card>
            </MDBox>
            {currentChartPreviewData?.chart_long_desc && (
              <MDTypography
                width='fit-content'
                variant='h5'
                color='info'
                sx={{
                  overflowWrap: 'anywhere',
                  whiteSpace: 'pre-wrap',
                  my: 1,
                  textOverflow: 'ellipsis',
                  overflow: 'hidden',
                  display: '-webkit-box',
                  WebkitLineClamp: 5,
                  WebkitBoxOrient: 'vertical'
                }}
              >
                {currentChartPreviewData?.chart_long_desc}
              </MDTypography>
            )}
            <Footer socials={false} />
          </Grid>
        </Grid>
      </MDBox>
      <Snackbar
        open={openSuccessSnackBar}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'right'
        }}
      >
        <Alert
          severity='success'
          sx={{ width: '100%' }}
          onClose={handleCloseSnackbar}
        >
          You can view this chart and leave comments.
        </Alert>
      </Snackbar>
    </>
  );
};

export default ChartPreview;
